/**
 * @module lib/api
 * @description Centralized API client for EmberPOS that automatically attaches
 * the X-Location-Id header from the location store to every request.
 * All components should use these helpers instead of raw fetch() when
 * making requests that need to be location-scoped.
 *
 * Also provides offline-aware API helpers that fall back to IndexedDB
 * when the server is unreachable, queuing transactions for later sync.
 */

import { API_BASE_URL } from '../config';
import { useLocationStore } from '../stores/useLocationStore';
import { isOnline } from '../services/connectivityService';
import { getRequestToken, getStaffToken, getTokenTenantId } from './authToken';
import useStore from '../stores/useStore';
import {
    queueOrder,
    queuePayment,
    queueOrderUpdate,
    cacheFullMenu,
    getCachedFullMenu,
} from '../services/offlineDb';

/**
 * Returns the current numeric location ID from the store.
 * Falls back to 1 (default location) if not set.
 * @returns {number}
 */
function getCurrentLocationId() {
    const state = useLocationStore.getState();
    return state.currentLocationId ?? 1;
}

/**
 * Builds the request headers: JSON content type, X-Location-Id, and
 * `Authorization: Bearer <staff token || device token>` when one is stored.
 * Caller-supplied headers win.
 * @param {Object} [extraHeaders={}] - Additional headers to merge
 * @returns {Object} Merged headers object
 */
export function buildHeaders(extraHeaders = {}) {
    const token = getRequestToken();
    return {
        'Content-Type': 'application/json',
        'X-Location-Id': String(getCurrentLocationId()),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...extraHeaders,
    };
}

/**
 * fetch() for the helpers below. A 401 TOKEN_INVALID (the tenant middleware's
 * answer to an expired/revoked token) while a staff token is stored means the
 * staff session is dead: end it and return to PIN entry instead of leaving the
 * POS "signed in" with every call failing. Any other 401 (e.g. refunds' "Invalid
 * manager PIN", a wrong PIN at login) is the caller's to show and never logs
 * the server out. Reads a clone so the caller still gets an unread body.
 */
async function send(path, init) {
    const res = await fetch(`${API_BASE_URL}${path}`, init);
    if (res?.status === 401 && getStaffToken() && await isTokenRejection(res)) {
        useStore.getState().expireStaffSession();
    }
    return res;
}

async function isTokenRejection(res) {
    try {
        const body = await res.clone().json();
        return body?.code === 'TOKEN_INVALID';
    } catch {
        return false;
    }
}

/**
 * fetch() replacement for tenant-scoped API calls: `path` is relative to
 * API_BASE_URL ('/api/...'), the init is passed through, and the auth +
 * X-Location-Id headers are merged in (caller headers win). Returns the raw
 * Response, so it drops in for `fetch(\`${API_BASE_URL}/api/...\`, init)`.
 * Raw fetch() to the API outside this module is guarded by
 * src/noRawApiFetch.test.js.
 * @param {string} path - API path (e.g., '/api/orders')
 * @param {RequestInit} [init={}]
 * @returns {Promise<Response>}
 */
export async function apiFetch(path, init = {}) {
    return send(path, { ...init, headers: buildHeaders(init.headers) });
}

/**
 * Performs a location-scoped GET request.
 * @param {string} path - API path (e.g., '/api/orders')
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiGet(path, options = {}) {
    return send(path, {
        ...options,
        method: 'GET',
        headers: buildHeaders(options.headers),
    });
}

/**
 * Performs a location-scoped POST request.
 * @param {string} path - API path
 * @param {Object} body - Request body (will be JSON.stringified)
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiPost(path, body, options = {}) {
    return send(path, {
        ...options,
        method: 'POST',
        headers: buildHeaders(options.headers),
        body: JSON.stringify(body),
    });
}

/**
 * Performs a location-scoped PUT request.
 * @param {string} path - API path
 * @param {Object} body - Request body (will be JSON.stringified)
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiPut(path, body, options = {}) {
    return send(path, {
        ...options,
        method: 'PUT',
        headers: buildHeaders(options.headers),
        body: JSON.stringify(body),
    });
}

/**
 * Performs a location-scoped PATCH request.
 * @param {string} path - API path
 * @param {Object} body - Request body (will be JSON.stringified)
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiPatch(path, body, options = {}) {
    return send(path, {
        ...options,
        method: 'PATCH',
        headers: buildHeaders(options.headers),
        body: JSON.stringify(body),
    });
}

/**
 * Performs a location-scoped DELETE request.
 * @param {string} path - API path
 * @param {Object} [options={}] - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiDelete(path, options = {}) {
    return send(path, {
        ...options,
        method: 'DELETE',
        headers: buildHeaders(options.headers),
    });
}

/**
 * Convenience: GET and parse JSON in one call.
 * @param {string} path - API path
 * @returns {Promise<*>} Parsed JSON response
 * @throws {Error} If the response is not ok
 */
export async function apiGetJson(path) {
    const res = await apiGet(path);
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || `API error: ${res.status}`);
    }
    return res.json();
}

/**
 * Convenience: POST and parse JSON in one call.
 * @param {string} path - API path
 * @param {Object} body - Request body
 * @returns {Promise<*>} Parsed JSON response
 * @throws {Error} If the response is not ok
 */
export async function apiPostJson(path, body) {
    const res = await apiPost(path, body);
    if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || `API error: ${res.status}`);
    }
    return res.json();
}

// ============================================
// OFFLINE-AWARE API HELPERS
// ============================================

/**
 * Create an order with offline fallback.
 * If the server is unreachable, the order is queued in IndexedDB
 * and will be synced automatically when connectivity resumes.
 *
 * @param {Object} orderPayload - The order data (tableId, serverId, serverName, guestCount, items, etc.)
 * @returns {Promise<{success: boolean, order: Object|null, offline: boolean, offlineId: number|null}>}
 */
export async function createOrderOfflineAware(orderPayload) {
    const locationId = getCurrentLocationId();

    // Try server first if we believe we are online
    if (isOnline()) {
        try {
            const response = await apiPost('/api/orders', orderPayload);
            if (response.ok) {
                const data = await response.json();
                return {
                    success: data.success,
                    order: data.order || null,
                    offline: false,
                    offlineId: null,
                };
            }
            // Server returned an error but was reachable -- do NOT queue offline
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                order: null,
                offline: false,
                offlineId: null,
                error: errorData.error || `Server error: ${response.status}`,
            };
        } catch {
            // Network error -- fall through to offline queue
            console.warn('[API] Server unreachable, queuing order offline');
        }
    }

    // Queue offline
    try {
        const offlineId = await queueOrder({ ...orderPayload, locationId });
        return {
            success: true,
            order: {
                id: `offline-${offlineId}`,
                offlineId,
                tableId: orderPayload.tableId,
                items: orderPayload.items,
                status: 'pending_sync',
                createdAt: new Date().toISOString(),
            },
            offline: true,
            offlineId,
        };
    } catch (err) {
        console.error('[API] Failed to queue order offline:', err);
        return {
            success: false,
            order: null,
            offline: true,
            offlineId: null,
            error: 'Failed to save order locally',
        };
    }
}

/**
 * Close an order (process payment) with offline fallback.
 *
 * @param {string|number} orderId - The server order ID (or "offline-N" for offline orders)
 * @param {Object} paymentPayload - { paymentMethod, tip, total, subtotal, tax, ... }
 * @returns {Promise<{success: boolean, offline: boolean}>}
 */
export async function closeOrderOfflineAware(orderId, paymentPayload) {
    const locationId = getCurrentLocationId();

    // If the order itself was created offline, always queue the payment offline too
    if (typeof orderId === 'string' && orderId.startsWith('offline-')) {
        try {
            await queuePayment({ orderId, locationId, ...paymentPayload });
            return { success: true, offline: true };
        } catch (err) {
            console.error('[API] Failed to queue payment offline:', err);
            return { success: false, offline: true };
        }
    }

    if (isOnline()) {
        try {
            const response = await apiPost(
                `/api/orders/${orderId}/close`,
                paymentPayload
            );
            if (response.ok) {
                const data = await response.json();
                return { success: data.success !== false, offline: false };
            }
            // Server returned error but was reachable
            const errorData = await response
                .json()
                .catch(() => ({ error: `Server error: ${response.status}` }));
            return {
                success: false,
                offline: false,
                error: errorData.error || `Server error: ${response.status}`,
            };
        } catch {
            console.warn('[API] Server unreachable, queuing payment offline');
        }
    }

    try {
        await queuePayment({ orderId, locationId, ...paymentPayload });
        return { success: true, offline: true };
    } catch (err) {
        console.error('[API] Failed to queue payment offline:', err);
        return { success: false, offline: true };
    }
}

/**
 * Send an order to the kitchen with offline fallback.
 *
 * @param {string|number} orderId - The server order ID
 * @returns {Promise<{success: boolean, offline: boolean}>}
 */
export async function sendToKitchenOfflineAware(orderId) {
    const locationId = getCurrentLocationId();

    if (isOnline()) {
        try {
            const response = await apiPost(`/api/orders/${orderId}/send`, {});
            if (response.ok) {
                return { success: true, offline: false };
            }
            return { success: false, offline: false };
        } catch {
            console.warn('[API] Server unreachable, queuing kitchen send offline');
        }
    }

    try {
        await queueOrderUpdate({
            endpoint: `/api/orders/${orderId}/send`,
            method: 'POST',
            body: {},
            locationId,
        });
        return { success: true, offline: true };
    } catch (err) {
        console.error('[API] Failed to queue kitchen send offline:', err);
        return { success: false, offline: true };
    }
}

/**
 * Fetch menu data with offline cache fallback.
 * On success, caches the result in IndexedDB for offline use.
 * On failure (offline), returns cached data if available.
 *
 * @param {string} [path='/api/menu'] - The menu endpoint path
 * @returns {Promise<{data: Object|null, offline: boolean, cached: boolean}>}
 */
export async function fetchMenuOfflineAware(path = '/api/menu') {
    const tenantKey = getTokenTenantId() || 'sample'; // no token = the sample tenant (D3)
    // Try the server first
    if (isOnline()) {
        try {
            const response = await apiGet(path);
            if (response.ok) {
                const data = await response.json();

                // Cache the menu data for offline use
                try {
                    await cacheFullMenu(data, tenantKey);
                } catch (cacheErr) {
                    console.warn('[API] Failed to cache menu data:', cacheErr);
                }

                return { data, offline: false, cached: false };
            }
        } catch {
            console.warn('[API] Server unreachable, falling back to cached menu');
        }
    }

    // Fall back to cached data
    try {
        const cached = await getCachedFullMenu(tenantKey);
        if (cached && cached.data) {
            console.log(`[API] Using cached menu data from ${cached.cachedAt}`);
            return { data: cached.data, offline: true, cached: true };
        }
    } catch (err) {
        console.error('[API] Failed to read cached menu:', err);
    }

    return { data: null, offline: true, cached: false };
}

// ============================================
// SPLIT PAYMENT API HELPERS
// ============================================

/**
 * Process a complete split payment for an order.
 * Sends all payment splits to the server at once.
 *
 * @param {string} orderId - The order UUID
 * @param {Object} splitData - Split payment data
 * @param {Array<Object>} splitData.payments - Array of payment splits
 * @param {number} [splitData.tip=0] - Total tip
 * @param {string} [splitData.processedByName] - Employee name
 * @returns {Promise<Object>} Server response with order and payment records
 */
export async function processSplitPayment(orderId, splitData) {
    return apiPostJson(`/api/orders/${orderId}/split-payment`, splitData);
}

/**
 * Process a single payment in a sequential split payment flow.
 *
 * @param {string} orderId - The order UUID
 * @param {Object} paymentData - Single payment data
 * @param {string} paymentData.method - Payment method
 * @param {number} paymentData.amount - Payment amount
 * @param {number} [paymentData.tip=0] - Tip amount
 * @param {number} paymentData.sequenceNumber - Sequence position (1-based)
 * @param {string} [paymentData.processedByName] - Employee name
 * @returns {Promise<Object>} Server response with payment record and remaining balance
 */
export async function processSingleSplitPayment(orderId, paymentData) {
    return apiPostJson(`/api/orders/${orderId}/split-payment/single`, paymentData);
}

/**
 * Get all payments for an order (split payment records).
 *
 * @param {string} orderId - The order UUID
 * @returns {Promise<Object>} Server response with payments array
 */
export async function getOrderPayments(orderId) {
    return apiGetJson(`/api/orders/${orderId}/payments`);
}

/**
 * Get a payment summary for an order with breakdown by method.
 *
 * @param {string} orderId - The order UUID
 * @returns {Promise<Object>} Payment summary with totals and per-method breakdown
 */
export async function getPaymentSummary(orderId) {
    return apiGetJson(`/api/orders/${orderId}/payments/summary`);
}
